import { useState, useCallback, useEffect, useMemo } from "react";
import { Plus } from "lucide-react";
import { useLocale } from "../../context/LanguageContext";
import {
  ItemCatalogGrid,
  CreateItemDrawer,
  QuickCreateTemplates,
  DeactivateConfirmDialog,
  useAdminMarketplaceItems,
  useCreateMarketplaceItem,
  useUpdateMarketplaceItem,
  useDeactivateMarketplaceItem,
  useImageUpload,
} from "../../features/marketplace";
import type {
  MarketplaceItem,
  MarketplaceCategory,
  ItemTemplate,
} from "../../features/marketplace";
import { useAllOrganizations } from "../../features/organizations";

type ScopeMode = "all" | "global" | "org";

/**
 * Platform-admin Reward Catalog: create/manage GLOBAL and ORGANIZATION-scoped
 * rewards across every organization. ADMIN-only (route-guarded). The per-org
 * Rewards page (routes/rewards.tsx) remains the ORG_ADMIN surface.
 */
export function AdminRewardCatalogPage() {
  const { t } = useLocale();

  // ─── Search & Filter State ──────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] =
    useState<MarketplaceCategory | null>(null);
  const [scopeMode, setScopeMode] = useState<ScopeMode>("all");
  const [orgFilterId, setOrgFilterId] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // ─── Data Fetching ──────────────────────────────────────────────────────
  const { data, loading } = useAdminMarketplaceItems({
    search: debouncedSearch || undefined,
    category: categoryFilter ?? undefined,
    limit: 100,
  });

  // NOTE: the allOrganizations resolver caps results at 50 (Math.min(limit, 50)),
  // so this flat picker + the badge join below only cover the first 50 orgs.
  // Fine while org count is low. At scale (>~50 orgs), replace with: (1) a
  // searchable combobox using allOrganizations({ search }), and (2) a batched
  // `MarketplaceItem.organization { _id name }` field resolver (organizationLoader
  // already exists in context) so badges read item.organization instead of joining.
  const { data: orgData } = useAllOrganizations({ limit: 50 });
  const organizations = useMemo(
    () =>
      (orgData?.allOrganizations?.items ?? []).map((o) => ({
        _id: o._id,
        name: o.name,
      })),
    [orgData],
  );
  const orgNameById = useMemo(
    () => Object.fromEntries(organizations.map((o) => [o._id, o.name])),
    [organizations],
  );

  const allItems = data?.adminMarketplaceItems?.items ?? [];
  const items = useMemo(() => {
    if (scopeMode === "global") {
      return allItems.filter((it) => it.scope === "GLOBAL");
    }
    if (scopeMode === "org" && orgFilterId) {
      return allItems.filter(
        (it) => it.scope === "ORGANIZATION" && it.organizationId === orgFilterId,
      );
    }
    return allItems;
  }, [allItems, scopeMode, orgFilterId]);

  // ─── Drawer State ──────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MarketplaceItem | undefined>();
  const [templateFill, setTemplateFill] = useState<ItemTemplate | null>(null);

  // ─── Deactivate Dialog ─────────────────────────────────────────────────
  const [deactivateTarget, setDeactivateTarget] =
    useState<MarketplaceItem | null>(null);

  // ─── Mutations ─────────────────────────────────────────────────────────
  const [createItem, { loading: creating }] = useCreateMarketplaceItem();
  const [updateItem, { loading: updating }] = useUpdateMarketplaceItem();
  const [deactivateItem, { loading: deactivating }] =
    useDeactivateMarketplaceItem();
  const { upload: uploadImage, uploading: uploadingImage } = useImageUpload({
    purpose: "MARKETPLACE",
  });

  // ─── Handlers ──────────────────────────────────────────────────────────

  const openCreateDrawer = useCallback(() => {
    setEditingItem(undefined);
    setTemplateFill(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((item: MarketplaceItem) => {
    setEditingItem(item);
    setTemplateFill(null);
    setDrawerOpen(true);
  }, []);

  const handleDuplicate = useCallback((item: MarketplaceItem) => {
    setEditingItem(undefined);
    setTemplateFill({
      name: `${item.name}${t("common.copySuffix")}`,
      description: item.description,
      shortDescription: item.shortDescription ?? "",
      koinsPrice: item.koinsPrice,
      category: item.category,
      fulfillmentType: item.fulfillmentType,
      fulfillmentInstructions: item.fulfillmentInstructions ?? "",
      emoji: "",
    });
    setDrawerOpen(true);
  }, [t]);

  const handleDeactivate = useCallback((item: MarketplaceItem) => {
    setDeactivateTarget(item);
  }, []);

  const handleTemplateSelect = useCallback((template: ItemTemplate) => {
    setEditingItem(undefined);
    setTemplateFill(template);
    setDrawerOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (input: Record<string, unknown>, imageFile?: File | null) => {
      try {
        // Upload image first if provided
        if (imageFile) {
          // Backend requires a valid 24-hex-char ObjectId for the S3 key path.
          // For new items, generate a temporary ObjectId so the upload succeeds.
          const entityId =
            editingItem?._id ??
            Array.from(crypto.getRandomValues(new Uint8Array(12)))
              .map((b) => b.toString(16).padStart(2, "0"))
              .join("");
          input.imageUrl = await uploadImage(imageFile, entityId);
        }

        if (editingItem) {
          await updateItem({ variables: { id: editingItem._id, input } });
        } else {
          // scope + organizationId come straight from the drawer form.
          await createItem({ variables: { input } });
        }

        setDrawerOpen(false);
        setEditingItem(undefined);
        setTemplateFill(null);
      } catch {
        // Error surfaces via Apollo mutation error state; drawer stays open.
      }
    },
    [editingItem, uploadImage, updateItem, createItem],
  );

  const handleConfirmDeactivate = useCallback(async () => {
    if (!deactivateTarget) return;
    try {
      await deactivateItem({ variables: { id: deactivateTarget._id } });
      setDeactivateTarget(null);
    } catch {
      // Error surfaces via Apollo mutation error state.
    }
  }, [deactivateTarget, deactivateItem]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t("rewardCatalog.title")}
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t("rewardCatalog.description")}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateDrawer}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          {t("rewardCatalog.createReward")}
        </button>
      </div>

      {/* Scope filter */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-border p-0.5">
          {(["all", "global"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => {
                setScopeMode(mode);
                setOrgFilterId("");
              }}
              className={`h-8 rounded px-3 text-sm font-medium transition-colors ${
                scopeMode === mode
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {mode === "all"
                ? t("rewardCatalog.filterAll")
                : t("rewardCatalog.filterGlobal")}
            </button>
          ))}
        </div>
        <select
          value={scopeMode === "org" ? orgFilterId : ""}
          onChange={(e) => {
            const id = e.target.value;
            if (id) {
              setScopeMode("org");
              setOrgFilterId(id);
            } else {
              setScopeMode("all");
              setOrgFilterId("");
            }
          }}
          className="h-9 rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">{t("rewardCatalog.filterOrgPlaceholder")}</option>
          {organizations.map((org) => (
            <option key={org._id} value={org._id}>
              {org.name}
            </option>
          ))}
        </select>
      </div>

      {/* Quick Create Templates */}
      <QuickCreateTemplates onSelect={handleTemplateSelect} />

      {/* Item Catalog Grid */}
      <ItemCatalogGrid
        items={items}
        total={items.length}
        isLoading={loading}
        search={search}
        categoryFilter={categoryFilter}
        onSearchChange={setSearch}
        onCategoryFilterChange={setCategoryFilter}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDeactivate={handleDeactivate}
        orgNameById={orgNameById}
      />

      {/* Create / Edit Drawer */}
      <CreateItemDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingItem(undefined);
          setTemplateFill(null);
        }}
        onSubmit={handleSubmit}
        loading={creating || updating || uploadingImage}
        item={editingItem}
        templateFill={templateFill}
        currentOrgId={null}
        isOrgAdmin={false}
        organizations={organizations}
      />

      {/* Deactivate Confirmation */}
      {deactivateTarget && (
        <DeactivateConfirmDialog
          item={deactivateTarget}
          onConfirm={handleConfirmDeactivate}
          onCancel={() => setDeactivateTarget(null)}
          loading={deactivating}
        />
      )}
    </div>
  );
}
